import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Play, Loader2, Repeat, Power, Receipt } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/format";

const RECURRENCES = [
  { v: "daily", label: "Daily" },
  { v: "weekly", label: "Weekly" },
  { v: "monthly", label: "Monthly" },
  { v: "quarterly", label: "Quarterly" },
  { v: "yearly", label: "Yearly" },
];

export default function RecurringTasksPage() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [invRule, setInvRule] = useState(null); // rule currently being invoiced

  const load = () => api.get("/recurring-tasks").then(({ data }) => setRules(data));
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/users").then(({ data }) => setUsers(data));
  }, []);

  const [form, setForm] = useState({
    title: "", description: "",
    client_id: "none", assignee_id: "none",
    priority: "medium", recurrence: "monthly", interval: 1,
    day_of_month: 15, due_days_offset: 10,
    starts_on: new Date().toISOString().slice(0, 10),
    default_bill_rate: "",
  });

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        client_id: form.client_id === "none" ? null : form.client_id,
        assignee_id: form.assignee_id === "none" ? null : form.assignee_id,
        priority: form.priority,
        recurrence: form.recurrence,
        interval: Number(form.interval) || 1,
        day_of_month: form.recurrence === "monthly" || form.recurrence === "quarterly" ? Number(form.day_of_month) : null,
        due_days_offset: Number(form.due_days_offset) || 7,
        starts_on: new Date(form.starts_on).toISOString(),
        default_bill_rate: form.default_bill_rate === "" ? null : Number(form.default_bill_rate),
      };
      await api.post("/recurring-tasks", payload);
      toast.success("Recurring rule created");
      setOpen(false);
      setForm({ ...form, title: "", description: "", default_bill_rate: "" });
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (r) => {
    await api.patch(`/recurring-tasks/${r.id}`, { active: !r.active });
    load();
  };

  const runNow = async (r) => {
    await api.post(`/recurring-tasks/${r.id}/run-now`);
    toast.success(`Task generated`);
    load();
  };

  const del = async (r) => {
    if (!window.confirm(`Delete recurring rule "${r.title}"?`)) return;
    await api.delete(`/recurring-tasks/${r.id}`);
    toast.success("Deleted");
    load();
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const userName = (id) => users.find((u) => u.id === id)?.full_name || "—";

  return (
    <div className="space-y-4" data-testid="recurring-page">
      <div className="flex items-center gap-3">
        <p className="text-sm text-muted-foreground flex-1">Rules automatically generate tasks on schedule. The engine ticks every 60 seconds.</p>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-recurring-btn"><Plus size={13} className="mr-1.5" /> New rule</Button>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground w-10"></th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Title</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Client</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Assignee</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Recurrence</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Next run</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Rate</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Generated</th>
              <th className="px-3 py-2 text-xs font-medium text-muted-foreground text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-12 text-center text-sm text-muted-foreground">
                <Repeat className="inline mr-2 opacity-40" size={16} />
                No recurring rules yet. <button onClick={() => setOpen(true)} className="underline">Create one</button> to automate monthly filings.
              </td></tr>
            )}
            {rules.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`rule-row-${r.id}`}>
                <td className="px-3 py-2.5"><Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`rule-toggle-${r.id}`} /></td>
                <td className="px-3 py-2.5 font-medium">{r.title}</td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.client_id ? clientName(r.client_id) : "—"}</td>
                <td className="px-3 py-2.5 text-xs">{r.assignee_id ? userName(r.assignee_id) : "—"}</td>
                <td className="px-3 py-2.5 text-xs capitalize"><Badge variant="secondary" className="text-[10px]">{r.recurrence}</Badge></td>
                <td className="px-3 py-2.5 text-xs font-mono text-muted-foreground">{formatDate(r.next_run_at, "dd MMM yyyy")}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{r.default_bill_rate ? `₹${Number(r.default_bill_rate).toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{r.generated_count || 0}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-amber-600 hover:text-amber-700 hover:bg-amber-500/10" onClick={() => setInvRule(r)} data-testid={`rule-invoice-${r.id}`} title="Invoice completed tasks"><Receipt size={12} /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => runNow(r)} disabled={!r.active} data-testid={`rule-run-${r.id}`} title="Run now"><Play size={12} /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del(r)} data-testid={`rule-delete-${r.id}`} title="Delete"><Power size={12} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-recurring-dialog">
          <DialogHeader>
            <DialogTitle>New recurring rule</DialogTitle>
            <DialogDescription>Automatically creates a task on schedule. Perfect for GST/ITR/TDS filings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Task title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Monthly GSTR-3B filing" data-testid="rec-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="rec-client"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Assignee</Label>
                <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger data-testid="rec-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Recurrence</Label>
                <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                  <SelectTrigger data-testid="rec-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{RECURRENCES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(form.recurrence === "monthly" || form.recurrence === "quarterly") && (
                <div className="space-y-1.5"><Label className="text-xs">Day of month</Label><Input type="number" min={1} max={28} value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: Number(e.target.value) })} data-testid="rec-day" /></div>
              )}
              <div className="space-y-1.5"><Label className="text-xs">Due after (days)</Label><Input type="number" min={0} max={365} value={form.due_days_offset} onChange={(e) => setForm({ ...form, due_days_offset: Number(e.target.value) })} data-testid="rec-due-offset" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Starts on</Label><Input type="date" value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} data-testid="rec-starts" /></div>
              <div className="space-y-1.5">
                <Label className="text-xs">Default bill rate (₹ per task, optional)</Label>
                <Input type="number" min={0} step="1" value={form.default_bill_rate} onChange={(e) => setForm({ ...form, default_bill_rate: e.target.value })} placeholder="e.g. 2500" data-testid="rec-bill-rate" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={busy || !form.title} data-testid="rec-submit">{busy && <Loader2 size={12} className="animate-spin mr-1.5" />} Create rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {invRule && (
        <InvoiceFromRuleDialog
          rule={invRule}
          clients={clients}
          onClose={() => setInvRule(null)}
          onCreated={(inv) => {
            setInvRule(null);
            toast.success(`Invoice ${inv.invoice_number} created`);
            navigate("/invoices");
          }}
        />
      )}
    </div>
  );
}


function InvoiceFromRuleDialog({ rule, clients, onClose, onCreated }) {
  const [preview, setPreview] = useState(null);
  const [rate, setRate] = useState(rule.default_bill_rate ?? "");
  const [clientId, setClientId] = useState(rule.client_id || "");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/recurring-tasks/${rule.id}/invoiceable`).then(({ data }) => {
      setPreview(data);
      // pre-select all tasks matching the chosen client
      const forClient = data.tasks.filter((t) => !clientId || t.client_id === clientId);
      setSelectedIds(new Set(forClient.map((t) => t.id)));
    });
  }, [rule.id, clientId]);

  useEffect(() => {
    if (!preview) return;
    const forClient = preview.tasks.filter((t) => !clientId || t.client_id === clientId);
    setSelectedIds(new Set(forClient.map((t) => t.id)));
  }, [clientId, preview]);

  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  const clientName = (id) => clients.find((c) => c.id === id)?.name || "—";
  const eligibleForClient = preview ? preview.tasks.filter((t) => !clientId || t.client_id === clientId) : [];
  const gstRate = preview?.suggested_gst_rate ?? 18;
  const subtotal = Number(rate || 0) * selectedIds.size;
  const gst = subtotal * gstRate / 100;
  const total = subtotal + gst;

  // Client options: distinct clients found on eligible tasks, plus rule.client_id if set
  const clientOptions = (() => {
    const set = new Set();
    if (rule.client_id) set.add(rule.client_id);
    (preview?.tasks || []).forEach((t) => { if (t.client_id) set.add(t.client_id); });
    return Array.from(set);
  })();

  const submit = async () => {
    if (!clientId) return toast.error("Select a client");
    if (!rate || Number(rate) <= 0) return toast.error("Enter a positive billing rate");
    if (selectedIds.size === 0) return toast.error("Select at least one completed task");
    setBusy(true);
    try {
      const body = {
        rate_override: Number(rate),
        client_id: clientId,
        invoice_date: invoiceDate,
        due_date: dueDate || null,
        task_ids: Array.from(selectedIds),
      };
      const { data } = await api.post(`/recurring-tasks/${rule.id}/invoice`, body);
      onCreated(data.invoice);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to create invoice"));
    } finally {
      setBusy(false);
    }
  };

  const toggleTask = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="invoice-from-rule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={16} className="text-amber-600" />
            Invoice from recurring rule
          </DialogTitle>
          <DialogDescription>
            Bundle completed tasks of <span className="font-medium text-foreground">{rule.title}</span> into a single draft invoice.
          </DialogDescription>
        </DialogHeader>

        {!preview && <div className="py-10 text-center text-sm text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={14} />Loading eligible tasks…</div>}

        {preview && preview.eligible_count === 0 && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No completed, un-invoiced tasks tied to this rule yet.
            <div className="text-xs mt-2">Mark a generated task as <span className="font-mono">done</span> to make it invoiceable.</div>
          </div>
        )}

        {preview && preview.eligible_count > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Client</Label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger data-testid="ifr-client"><SelectValue placeholder="Select client…" /></SelectTrigger>
                  <SelectContent>
                    {clientOptions.map((id) => <SelectItem key={id} value={id}>{clientName(id)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Rate per task (₹)</Label>
                <Input type="number" min={0} step="1" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="ifr-rate" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Invoice date</Label>
                <Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} data-testid="ifr-date" />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                Eligible tasks · {eligibleForClient.length} for this client
              </div>
              <div className="border border-border rounded-md max-h-72 overflow-y-auto">
                {eligibleForClient.length === 0 && (
                  <div className="p-4 text-center text-xs text-muted-foreground">No tasks match this client.</div>
                )}
                {eligibleForClient.map((t) => (
                  <label key={t.id} className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-0 hover:bg-muted/40 cursor-pointer text-sm" data-testid={`ifr-task-${t.id}`}>
                    <input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleTask(t.id)} className="accent-primary" />
                    <span className="flex-1">{t.title}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {t.completed_at ? formatDate(t.completed_at, "dd MMM yyyy") : "—"}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-6 border-t border-border pt-3 text-sm">
              <div className="text-muted-foreground">Subtotal <span className="font-mono ml-1">{money(subtotal)}</span></div>
              <div className="text-muted-foreground">GST ({gstRate}%) <span className="font-mono ml-1">{money(gst)}</span></div>
              <div className="font-heading font-semibold">Total <span className="font-mono ml-1">{money(total)}</span></div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {preview && preview.eligible_count > 0 && (
            <Button onClick={submit} disabled={busy || selectedIds.size === 0 || !rate || !clientId} data-testid="ifr-submit">
              {busy && <Loader2 size={12} className="animate-spin mr-1.5" />}
              Create draft invoice
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
